import { FC, useState } from "react";

import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

import HLSStreamPlayer from "../../../pages/data-hub/components/liveview/components/cameraView/HLSStreamPlayer";
import StyledLoadingButton from "../../providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { ViewModeEnum } from "./LiveViewContainer";
import ExtendButton from "./MaximizeButton";
import MediaStatus from "../media/MediaStatus";

interface LiveViewExpandedDialogProps {
  s3Key: string;
  viewMode: ViewModeEnum;
  expanded: boolean;
  changeViewMode?: () => void;
  changeExpandedMode: () => void;
  openPtController?: () => void;
}

const LiveViewExpandedDialog: FC<LiveViewExpandedDialogProps> = ({
  s3Key,
  viewMode,
  expanded,
  changeViewMode,
  changeExpandedMode,
}) => {
  const [videoLoading, setVideoLoading] = useState(false);

  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const isFullSizeViewMode =
    viewMode === ViewModeEnum.LIVE ||
    viewMode === ViewModeEnum.KEYFRAME ||
    viewMode === ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS;

  const getLiveViewDialogPContentStyles = () => {
    if (isFullSizeViewMode) {
      return {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 0,
      };
    }

    return {};
  };

  const liveViewDialogContentStyles = getLiveViewDialogPContentStyles();

  return (
    <Dialog
      id="LiveViewExpandedDialog"
      PaperProps={{
        sx: {
          margin: 0,
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100%",
          height: "100%",
          borderRadius: 0,
          backgroundColor: "black",
        },
      }}
      fullWidth
      open={expanded}
      onClose={changeExpandedMode}
    >
      <Box sx={{ position: "absolute", zIndex: 3, right: 8, top: 8 }}>
        <ExtendButton
          title="Minimize"
          changeExpandedMode={changeExpandedMode}
        />
      </Box>

      <DialogContent
        sx={{
          padding: 0,
          margin: "0 auto",
          overflow: "hidden",
          ...liveViewDialogContentStyles,
        }}
      >
        <Box sx={{ width: "100%", height: "100%" }}>
          {(viewMode === ViewModeEnum.KEYFRAME ||
            viewMode === ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS) && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                backgroundColor: "black",
              }}
            >
              {!imageError && s3Key && (
                <CardMedia
                  src={s3Key}
                  component="img"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              )}

              {imageError && (
                <MediaStatus
                  variant="keyframeLoading"
                  message="Image not found or failed to load"
                />
              )}
            </Box>
          )}

          {viewMode === ViewModeEnum.LIVE && (
            <HLSStreamPlayer
              videoLoading={videoLoading}
              setVideoLoading={setVideoLoading}
            />
          )}
        </Box>

        {changeViewMode && (
          <Box
            sx={{
              position: "fixed",
              left: "50%",
              bottom: "20px",
              transform: "translate(-50%, -50%)",
              margin: "0 auto",
              display: "flex",
              gap: "1em",
            }}
          >
            <StyledLoadingButton
              variant="contained"
              color="primary"
              size="small"
              onClick={changeViewMode}
            >
              {viewMode !== ViewModeEnum.LIVE ? "Activate" : "Stop"} live view
            </StyledLoadingButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LiveViewExpandedDialog;
